import { useEffect, useState, useCallback } from "react";
import { Loader2, Lock, LogOut, BarChart3, TrendingUp, AlertTriangle, Users } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { LOGO_SRC } from "@/components/Logo";
import { adminLogin, adminStats, adminAssessments } from "@/lib/api";
import { Seo } from "@/components/Seo";

const TOKEN_KEY = "scmbrain_admin_token";

const levelColor = (level = "") => {
  if (level.includes("Leading")) return "#1A6B5A";
  if (level.includes("Capable")) return "#3B82F6";
  if (level.includes("Developing")) return "#F59E0B";
  return "#EF4444";
};

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setStats(null);
    setRows([]);
  }, []);

  const loadData = useCallback(async (t) => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([adminStats(t), adminAssessments(t)]);
      setStats(s);
      setRows(a.assessments);
    } catch (e) {
      if (e?.response?.status === 401) { toast.error("Session expired. Please sign in again."); logout(); }
      else toast.error("Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) loadData(token);
  }, [token, loadData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!password) return;
    setAuthLoading(true);
    try {
      const { token: t } = await adminLogin(password);
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      setPassword("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Incorrect password");
    } finally {
      setAuthLoading(false);
    }
  };

  // ---------- Login screen ----------
  if (!token) {
    return (
      <div className="min-h-screen bg-[#1B3A6B] flex items-center justify-center px-6">
        <Seo title="Admin · SCMBrain" description="SCMBrain admin dashboard." path="/admin" noindex />
        <form onSubmit={handleLogin} data-testid="admin-login-form"
          className="w-full max-w-sm bg-white rounded-md p-8 shadow-2xl">
          <img src={LOGO_SRC} alt="SCMBrain" className="h-9 w-auto mb-6" />
          <div className="flex items-center gap-2 mb-1">
            <Lock className="h-4 w-4 text-[#1A6B5A]" />
            <h1 className="font-heading text-xl font-bold text-[#1B3A6B]">Admin Dashboard</h1>
          </div>
          <p className="text-sm text-[#3E577A] mb-6">Enter the admin password to continue.</p>
          <Input
            data-testid="admin-password-input" type="password" autoFocus
            placeholder="Admin password" value={password}
            onChange={(e) => setPassword(e.target.value)} className="mb-4"
          />
          <button
            data-testid="admin-login-btn" type="submit" disabled={authLoading}
            className="w-full bg-[#1A6B5A] text-white hover:bg-[#135245] transition-colors rounded-sm px-5 py-3 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {authLoading ? (<><Loader2 className="h-4 w-4 animate-spin" /> Signing in…</>) : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  // ---------- Dashboard ----------
  return (
    <div className="min-h-screen bg-[#F4F4F4]">
      <Seo title="Admin · SCMBrain" description="SCMBrain admin dashboard." path="/admin" noindex />
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_SRC} alt="SCMBrain" className="h-8 w-auto" />
            <span className="text-sm font-semibold text-[#3E577A] border-l border-[#E5E7EB] pl-3">Admin</span>
          </div>
          <button data-testid="admin-logout-btn" onClick={logout}
            className="inline-flex items-center gap-2 text-sm text-[#3E577A] hover:text-[#1B3A6B] font-medium">
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10" data-testid="admin-dashboard">
        {loading || !stats ? (
          <div className="flex items-center justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#1A6B5A]" /></div>
        ) : (
          <>
            <h1 className="font-heading text-3xl font-extrabold text-[#1B3A6B] mb-8">Overview</h1>

            {/* METRIC CARDS */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
              <Metric testid="metric-total" icon={BarChart3} label="Assessments completed" value={stats.total_assessments} />
              <Metric testid="metric-conversion" icon={TrendingUp} label="Free → Paid conversion" value={`${stats.conversion_rate}%`}
                sub={`${stats.paid_count} paid · ${stats.free_count} free`} />
              <Metric testid="metric-paid" icon={Users} label="Paid reports" value={stats.paid_count} />
              <Metric testid="metric-interest" icon={Users} label="Interested in paid" value={stats.interested_in_paid} />
            </div>

            <div className="grid lg:grid-cols-2 gap-6 mb-10">
              {/* AVG OMS BY FUNCTION */}
              <Panel title="Average OMS by function" subtitle="Lower OMS = more mature">
                <BarList
                  items={stats.avg_oms_by_function.map((f) => ({ label: f.function, value: f.avg_oms, meta: `${f.count} assessed` }))}
                  max={9} suffix="" color="#1B3A6B" testid="avg-oms-list"
                />
              </Panel>

              {/* TOP CRITICAL RISK ZONE L2 */}
              <Panel title="Most common Critical Risk Zone sub-functions" subtitle="High complexity + low maturity">
                {stats.top_critical_risk_l2.length === 0 ? (
                  <Empty text="No sub-functions in the Critical Risk Zone yet." />
                ) : (
                  <BarList
                    items={stats.top_critical_risk_l2.map((c) => ({ label: c.name, value: c.count, meta: `${c.count} user${c.count === 1 ? "" : "s"}` }))}
                    max={Math.max(...stats.top_critical_risk_l2.map((c) => c.count))} color="#EF4444" testid="crz-list"
                  />
                )}
              </Panel>

              {/* BY INDUSTRY */}
              <Panel title="Completions by industry">
                <BarList
                  items={stats.by_industry.map((i) => ({ label: i.industry, value: i.count, meta: `${i.count}` }))}
                  max={Math.max(1, ...stats.by_industry.map((i) => i.count))} color="#1A6B5A" testid="industry-list"
                />
              </Panel>

              {/* BY COMPANY SIZE */}
              <Panel title="Completions by company size">
                <BarList
                  items={stats.by_company_size.map((i) => ({ label: i.company_size, value: i.count, meta: `${i.count}` }))}
                  max={Math.max(1, ...stats.by_company_size.map((i) => i.count))} color="#3B82F6" testid="size-list"
                />
              </Panel>
            </div>

            {/* ASSESSMENTS TABLE */}
            <Panel title={`All assessments (${rows.length})`}>
              <div className="overflow-x-auto -mx-2">
                <table className="w-full text-sm" data-testid="assessments-table">
                  <thead>
                    <tr className="text-left text-[#94A3B8] border-b border-[#E5E7EB]">
                      <Th>Name</Th><Th>Company</Th><Th>Email</Th><Th>Industry</Th>
                      <Th>OMS</Th><Th>PES</Th><Th>Maturity</Th><Th>Payment</Th><Th>Date</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} data-testid="assessment-row" className="border-b border-[#F0F0F0] hover:bg-[#F9FAFB]">
                        <Td className="font-medium text-[#1B3A6B]">{r.name}</Td>
                        <Td>{r.company}</Td>
                        <Td className="text-[#3E577A]">{r.email}</Td>
                        <Td>{r.industry}</Td>
                        <Td className="font-semibold">{r.overall_oms}</Td>
                        <Td>{r.overall_pes}</Td>
                        <Td>
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-white whitespace-nowrap" style={{ background: levelColor(r.maturity_level) }}>
                            {r.maturity_level?.replace("Level ", "L")}
                          </span>
                        </Td>
                        <Td>
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${r.payment_status === "paid" ? "bg-[#E6F2EF] text-[#1A6B5A]" : "bg-[#F4F4F4] text-[#3E577A]"}`}>
                            {r.payment_status}
                          </span>
                        </Td>
                        <Td className="text-[#94A3B8] whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</Td>
                      </tr>
                    ))}
                    {rows.length === 0 && (
                      <tr><td colSpan={9} className="py-10 text-center text-[#94A3B8]">No assessments yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Panel>
          </>
        )}
      </main>
    </div>
  );
}

const Metric = ({ icon: Icon, label, value, sub, testid }) => (
  <div data-testid={testid} className="bg-white border border-[#E5E7EB] rounded-sm p-5">
    <div className="flex items-center justify-between">
      <span className="text-xs uppercase tracking-wider text-[#94A3B8] font-semibold">{label}</span>
      <Icon className="h-4 w-4 text-[#1A6B5A]" />
    </div>
    <div className="font-heading text-3xl font-extrabold text-[#1B3A6B] mt-2">{value}</div>
    {sub && <div className="text-xs text-[#3E577A] mt-1">{sub}</div>}
  </div>
);

const Panel = ({ title, subtitle, children }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-sm p-6">
    <h2 className="font-heading font-bold text-[#1B3A6B]">{title}</h2>
    {subtitle && <p className="text-xs text-[#94A3B8] mt-0.5 mb-4">{subtitle}</p>}
    <div className={subtitle ? "" : "mt-4"}>{children}</div>
  </div>
);

const BarList = ({ items, max, color, testid }) => (
  <div className="space-y-3" data-testid={testid}>
    {items.map((it, i) => (
      <div key={i}>
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-[#1B3A6B] truncate pr-3">{it.label}</span>
          <span className="text-[#3E577A] font-semibold whitespace-nowrap">{it.value} <span className="text-[#94A3B8] font-normal text-xs">{it.meta}</span></span>
        </div>
        <div className="h-2 bg-[#F0F0F0] rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${Math.min(100, (it.value / max) * 100)}%`, background: color }} />
        </div>
      </div>
    ))}
  </div>
);

const Empty = ({ text }) => <p className="text-sm text-[#94A3B8] py-4">{text}</p>;
const Th = ({ children }) => <th className="py-2 px-2 font-semibold whitespace-nowrap">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`py-2.5 px-2 ${className}`}>{children}</td>;
